import React, {useState} from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography
} from "@mui/material";
import {AddCircleOutline as GanhaIcon, RemoveCircleOutline as PerdeIcon} from "@mui/icons-material";
import {listaCurta, nomeDaAssinatura, nomeDoPerfil, plural} from "../sistemaPagina";
import {getBackendEntityMessage} from "../sistemasUtils";

const Linha = ({ganha, children}) => (
    <Stack component="li" direction="row" spacing={1} alignItems="flex-start">
        {ganha ? <GanhaIcon fontSize="small" color="success" /> : <PerdeIcon fontSize="small" color="error" />}
        <Typography variant="body2">{children}</Typography>
    </Stack>
);

// Quem ganha ou perde acesso, como a prévia `GET /funcionalidades/{id}/efeito` responde (BBF-173).
export const EfeitoDoMovimento = ({efeito}) => {
    if (!efeito) return null;
    const nome = efeito.funcionalidade?.nome;
    const ganham = efeito.assinaturas?.ganham || [];
    const perdem = efeito.assinaturas?.perdem || [];
    const perfisGanham = efeito.perfis?.ganham || [];
    const perfisPerdem = efeito.perfis?.perdem || [];
    const assinaturas = [...ganham, ...perdem];

    if (!efeito.completa) {
        return (
            <Typography variant="body2" color="text.secondary">
                A funcionalidade está em construção, sem endpoint, então ninguém tem acesso a ela hoje. Nada muda para as assinaturas nem para os perfis.
            </Typography>
        );
    }
    if (efeito.nada_muda) {
        return <Typography variant="body2">Ninguém ganha nem perde acesso.</Typography>;
    }
    return (
        <Stack component="ul" spacing={1} sx={{listStyle: 'none', p: 0, m: 0}}>
            {ganham.length ? <Linha ganha>{plural(ganham.length, 'assinatura passa', 'assinaturas passam')} a ter {nome}: {listaCurta(ganham.map(nomeDaAssinatura))}.</Linha> : null}
            {perfisGanham.length ? <Linha ganha>{plural(perfisGanham.length, 'perfil passa', 'perfis passam')} a conceder: {listaCurta(perfisGanham.map((perfil) => nomeDoPerfil(perfil, assinaturas)))}.</Linha> : null}
            {perdem.length ? <Linha>{plural(perdem.length, 'assinatura perde', 'assinaturas perdem')} {nome}: {listaCurta(perdem.map(nomeDaAssinatura))}.</Linha> : null}
            {perfisPerdem.length ? <Linha>{plural(perfisPerdem.length, 'perfil deixa', 'perfis deixam')} de conceder: {listaCurta(perfisPerdem.map((perfil) => nomeDoPerfil(perfil, assinaturas)))}.</Linha> : null}
        </Stack>
    );
};

/*
 * Confirmação de mover ou remover. `carregando` cobre a espera da prévia; o erro da API ao confirmar
 * (409 do módulo com funcionalidade, 400 de outro sistema) aparece aqui dentro, junto do que o causou.
 */
export default function ConfirmacaoDialog({aberto, titulo, carregando = false, botao, perigo = false, bloqueado = false, onConfirmar, onFechar, children}) {
    const [erro, setErro] = useState('');
    const [salvando, setSalvando] = useState(false);

    const fechar = () => {
        setErro('');
        onFechar();
    };

    const confirmar = async () => {
        setSalvando(true);
        try {
            await onConfirmar();
            setErro('');
        } catch (error) {
            setErro(getBackendEntityMessage(error, 'Não foi possível concluir.'));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Dialog open={aberto} onClose={fechar} fullWidth maxWidth="sm">
            <DialogTitle>{titulo}</DialogTitle>
            <DialogContent dividers>
                {carregando ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={18} />
                        <Typography color="text.secondary">Calculando quem ganha ou perde acesso…</Typography>
                    </Stack>
                ) : <Box sx={{display: 'grid', gap: 2}}>{children}</Box>}
                {erro ? <Alert severity="error" sx={{mt: 2}}>{erro}</Alert> : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={fechar}>Cancelar</Button>
                <Button variant="contained" color={perigo ? 'error' : 'primary'} onClick={confirmar} disabled={carregando || salvando || bloqueado}>
                    {botao}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
